import React from 'react';
import { ArrowRight, Play, TrendingUp, Activity, CheckCircle2, Clock, AlertTriangle, BarChart3 } from 'lucide-react';

export interface SiteHeroSectionProps {
  onOpenDemo: () => void;
  onOpenDiagnosis: () => void;
}

export function SiteHeroSection({ onOpenDemo, onOpenDiagnosis }: SiteHeroSectionProps) {
  return (
    <section id="inicio" className="relative overflow-hidden bg-white pb-10 pt-28 lg:pb-14">
      {/* Subtle dot grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          opacity: 0.35,
        }}
      />
      {/* Right fade */}
      <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-white via-white/60 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-10 lg:grid-cols-[1fr_1.08fr] lg:gap-14 xl:gap-20">
          {/* ── Left column ── */}
          <div className="mx-auto w-full max-w-xl text-center md:mx-0 md:max-w-none md:text-left">
            {/* Pill badge */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-1.5 md:mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.25)]" />
              <span className="text-[0.65rem] font-bold uppercase tracking-widest text-blue-700 sm:text-[0.7rem]">
                14 Módulos · Operação · Método · Estratégia
              </span>
            </div>

            <h1 className="mb-5 text-[1.75rem] font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-4xl md:mb-6 md:text-[1.85rem] lg:text-[2.35rem] xl:text-[2.75rem]">
              DataFlow — Engenharia, Simulação e Governança de Processos{" "}
              <span className="text-blue-600">Ponta a Ponta.</span>
            </h1>

            <p className="mb-8 text-base leading-relaxed text-slate-600 sm:text-lg md:mb-9">
              Da torre de controle ao desdobramento estratégico — projete fluxos otimizados, execute com
              disciplina gerencial, resolva falhas de forma estruturada e garanta a execução rígida com
              monitoramento de desvios, simulação preditiva e indicadores individuais em tempo real.
            </p>

            {/* CTA row */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center md:justify-start">
              <button
                type="button"
                onClick={onOpenDemo}
                className="inline-flex items-center justify-center gap-2 text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors px-6 py-3.5 rounded-md shadow-sm"
                style={{ fontWeight: 600, fontSize: "0.925rem" }}
              >
                Agendar Demonstração Técnica
                <ArrowRight size={15} strokeWidth={2.5} />
              </button>
              <button
                type="button"
                onClick={onOpenDiagnosis}
                className="inline-flex items-center justify-center gap-2 text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all px-6 py-3.5 rounded-md"
                style={{ fontWeight: 500, fontSize: "0.925rem" }}
              >
                <Play size={13} className="text-blue-600" fill="currentColor" />
                Solicitar Diagnóstico Operacional
              </button>
            </div>
          </div>

          {/* ── Right column: App Mockup ── */}
          <div className="relative mx-auto w-full max-w-lg md:max-w-none md:min-w-0">
            <div className="origin-top md:scale-[0.94] lg:scale-100">
              <AppMockup />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── App Mockup ─────────────────────────────────────────────── */
function AppMockup() {
  const barValues = [55, 72, 68, 84, 79, 91, 88, 95];
  const days = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom", "Seg"];

  const kpis = [
    { label: "Score Geral", value: "94.1", unit: "%", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
    { label: "Desvios Ativos", value: "5", unit: "", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
    { label: "Processos", value: "162", unit: "", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
    { label: "SLA em Risco", value: "1.8", unit: "%", color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-100" },
  ];

  const flows = [
    { name: "Aprovação de Compras", progress: 88, status: "ok", team: "Financeiro" },
    { name: "Onboarding Operacional", progress: 64, status: "warning", team: "RH" },
    { name: "Validação de Entregas", progress: 97, status: "ok", team: "Logística" },
    { name: "Auditoria de Processos", progress: 41, status: "critical", team: "Qualidade" },
  ];

  const statusIcon = (s: string) =>
    s === "ok" ? <CheckCircle2 size={10} className="text-emerald-500" /> :
    s === "warning" ? <AlertTriangle size={10} className="text-amber-500" /> :
    <Clock size={10} className="text-rose-500" />;

  const statusLabel = (s: string) =>
    s === "ok" ? "No prazo" : s === "warning" ? "Atenção" : "Crítico";

  const statusColor = (s: string) =>
    s === "ok" ? "text-emerald-600" : s === "warning" ? "text-amber-600" : "text-rose-600";

  const barColor = (i: number) => i === 7 ? "bg-blue-600" : i > 4 ? "bg-blue-400" : "bg-slate-200";

  return (
    <div className="relative">
      {/* Ambient glow */}
      <div className="absolute -top-8 -left-8 w-72 h-72 bg-blue-100 rounded-full opacity-40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -right-8 w-56 h-56 bg-indigo-100 rounded-full opacity-35 blur-3xl pointer-events-none" />

      {/* Browser chrome */}
      <div className="relative rounded-2xl border border-slate-200 shadow-2xl bg-white overflow-hidden" style={{ boxShadow: "0 32px 64px -12px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.06)" }}>
        {/* Chrome bar */}
        <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200/80">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="flex-1 mx-2">
            <div className="bg-white border border-slate-200 rounded-md px-3 py-1 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-slate-400" style={{ fontSize: "0.7rem" }}>app.dataflow.com.br/torre-de-controle</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Activity size={11} className="text-emerald-500" />
            <span className="text-emerald-600" style={{ fontSize: "0.68rem", fontWeight: 700 }}>AO VIVO</span>
          </div>
        </div>

        <div className="p-5 bg-white">
          {/* Page title */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-slate-900" style={{ fontWeight: 700, fontSize: "0.9rem" }}>Torre de Controle Operacional</p>
              <p className="text-slate-500" style={{ fontSize: "0.7rem" }}>Atualizado há 12 segundos</p>
            </div>
            <span className="bg-blue-50 text-blue-700 border border-blue-100 rounded px-2 py-0.5" style={{ fontSize: "0.65rem", fontWeight: 700 }}>
              2ª Feira, 10 Jun
            </span>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {kpis.map((k) => (
              <div key={k.label} className={`rounded-lg border ${k.border} ${k.bg} p-2.5`}>
                <p className="text-slate-500 mb-0.5" style={{ fontSize: "0.6rem", fontWeight: 600 }}>{k.label}</p>
                <p className={`${k.color}`} style={{ fontSize: "1.2rem", fontWeight: 800, lineHeight: 1 }}>
                  {k.value}<span style={{ fontSize: "0.65rem" }}>{k.unit}</span>
                </p>
              </div>
            ))}
          </div>

          {/* Bar chart */}
          <div className="mb-4 bg-slate-50 rounded-xl border border-slate-100 p-3.5">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-slate-700" style={{ fontSize: "0.72rem", fontWeight: 700 }}>Aderência Semanal</span>
              <div className="flex items-center gap-1">
                <TrendingUp size={10} className="text-emerald-500" />
                <span className="text-emerald-600" style={{ fontSize: "0.65rem", fontWeight: 700 }}>+5.2%</span>
              </div>
            </div>
            <div className="flex items-end gap-1 h-14">
              {barValues.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className={`w-full rounded-sm ${barColor(i)} transition-all`} style={{ height: `${(v / 100) * 48}px` }} />
                  <span className="text-slate-400" style={{ fontSize: "0.58rem" }}>{days[i]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Flow list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-700" style={{ fontSize: "0.72rem", fontWeight: 700 }}>Fluxos em Execução</span>
              <BarChart3 size={11} className="text-slate-400" />
            </div>
            <div className="flex flex-col gap-2.5">
              {flows.map((f) => (
                <div key={f.name}>
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="text-slate-800" style={{ fontSize: "0.7rem", fontWeight: 600 }}>{f.name}</span>
                      <span className="text-slate-400 ml-1.5" style={{ fontSize: "0.62rem" }}>{f.team}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {statusIcon(f.status)}
                      <span className={`${statusColor(f.status)}`} style={{ fontSize: "0.62rem", fontWeight: 700 }}>
                        {statusLabel(f.status)}
                      </span>
                    </div>
                  </div>
                  <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        f.status === "ok" ? "bg-emerald-500" : f.status === "warning" ? "bg-amber-500" : "bg-rose-500"
                      }`}
                      style={{ width: `${f.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating notification card */}
      <div
        className="absolute -bottom-5 -left-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl sm:-left-8"
        style={{ boxShadow: "0 8px 24px -4px rgba(15,23,42,0.14)" }}
      >
        <div className="w-7 h-7 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
          <AlertTriangle size={13} className="text-amber-500" />
        </div>
        <div>
          <p className="text-slate-900" style={{ fontSize: "0.72rem", fontWeight: 700 }}>Desvio detectado</p>
          <p className="text-slate-500" style={{ fontSize: "0.65rem" }}>Auditoria · SLA: 2h restantes</p>
        </div>
      </div>

      {/* Floating score badge */}
      <div
        className="absolute -right-4 -top-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl sm:-right-6"
        style={{ boxShadow: "0 8px 24px -4px rgba(15,23,42,0.14)" }}
      >
        <p className="text-slate-500 mb-0.5" style={{ fontSize: "0.62rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Score DataFlow</p>
        <p className="text-emerald-600" style={{ fontSize: "1.6rem", fontWeight: 900, lineHeight: 1 }}>94.1<span style={{ fontSize: "0.75rem" }}>%</span></p>
        <p className="text-emerald-600" style={{ fontSize: "0.62rem", fontWeight: 700 }}>↑ Alta aderência</p>
      </div>
    </div>
  );
}
